import { recordCountMetric } from "@/lib/monitoring/telemetry";

type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsProperties = Record<string, AnalyticsValue>;

/**
 * Represents a discrete event for product analytics tracking.
 * Used to measure user interactions and feature usage across the application.
 */
export type ProductAnalyticsEvent = {
  /** The unique identifier for the event (e.g., 'button_clicked', 'post_created'). */
  name: string;
  /** Optional contextual metadata associated with the event. */
  properties?: AnalyticsProperties;
};

/**
 * Defines the contract for an analytics client implementation.
 * Allows swapping the underlying analytics provider (e.g., Sentry, PostHog, Mixpanel) without changing consumer code.
 */
export interface ProductAnalyticsClient {
  /**
   * Records a single analytics event.
   *
   * @param event - The event payload containing the name and optional properties.
   */
  track(event: ProductAnalyticsEvent): void;
}

/**
 * Cleans and formats event properties before sending them to the analytics provider.
 * Removes nullish values and truncates excessively long strings to prevent payload bloat.
 *
 * @param properties - The raw properties object to sanitize.
 * @returns A new object containing only valid, sanitized properties.
 */
function sanitizeProperties(properties: AnalyticsProperties = {}): AnalyticsProperties {
  return Object.entries(properties).reduce<AnalyticsProperties>((acc, [key, value]) => {
    if (value === null || value === undefined) {
      return acc;
    }

    if (typeof value === "string") {
      acc[key] = value.slice(0, 120);
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
}

class SentryProductAnalyticsClient implements ProductAnalyticsClient {
  track(event: ProductAnalyticsEvent): void {
    recordCountMetric(event.name, 1, sanitizeProperties(event.properties));
  }
}

let productAnalyticsClient: ProductAnalyticsClient = new SentryProductAnalyticsClient();

/**
 * Overrides the default analytics client instance.
 * Useful for injecting mock clients during testing or switching providers at runtime.
 *
 * @param client - The new analytics client implementation to use globally.
 */
export function setProductAnalyticsClient(client: ProductAnalyticsClient): void {
  productAnalyticsClient = client;
}

/**
 * Tracks a user interaction or system event within the product.
 * This is the primary function developers should use to instrument the application.
 *
 * @param name - The concise, descriptive name of the event (e.g., 'user_login_success').
 * @param properties - Additional metadata to attach to the event (e.g., { method: 'email' }).
 *
 * @example
 * trackProductEvent('post_published', { templateId: 't-123', platform: 'instagram' });
 */
export function trackProductEvent(name: string, properties?: AnalyticsProperties): void {
  productAnalyticsClient.track({
    name,
    properties,
  });
}
